import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { shoppingApi, projectsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Check, Trash2, Pencil, X, ShoppingCart, FolderKanban, Plus, Eye, EyeOff,
} from "lucide-react";
import { cn } from "../lib/utils";

const STORAGE_KEY = "malair_shopping_project";

const ShoppingList = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [newText, setNewText] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editQuantity, setEditQuantity] = useState("");
  const [showDone, setShowDone] = useState(false);

  useEffect(() => { loadData(); }, []);

  // Remember selected project
  const selectProject = (id) => {
    setSelectedProject(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  };

  const loadData = async () => {
    try {
      const [itemsRes, projectsRes] = await Promise.all([
        shoppingApi.getAll(), projectsApi.getAll(),
      ]);
      setItems(itemsRes.data);
      const active = projectsRes.data.filter(p => p.is_active);
      setProjects(active);

      // Restore last project from localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && active.find(p => p.id === saved)) {
        setSelectedProject(saved);
      } else if (active.length > 0) {
        const undone = {};
        itemsRes.data.forEach(i => { if (!i.done) undone[i.project_id] = (undone[i.project_id] || 0) + 1; });
        const first = active.find(p => undone[p.id]) || active[0];
        setSelectedProject(first.id);
      }
    } catch { toast.error("Kunde inte ladda data"); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newText.trim() || !selectedProject) return;
    try {
      const res = await shoppingApi.create({ project_id: selectedProject, text: newText.trim(), quantity: newQuantity.trim() });
      setItems(prev => [res.data, ...prev]);
      setNewText("");
      setNewQuantity("");
    } catch { toast.error("Kunde inte lagga till"); }
  };

  const handleToggle = async (itemId) => {
    try {
      const res = await shoppingApi.toggle(itemId);
      setItems(prev => prev.map(i => i.id === itemId ? res.data : i));
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  const handleDelete = async (itemId) => {
    try {
      await shoppingApi.delete(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch { toast.error("Kunde inte ta bort"); }
  };

  const startEdit = (item, e) => {
    if (e) e.stopPropagation();
    setEditingId(item.id);
    setEditText(item.text);
    setEditQuantity(item.quantity || "");
  };

  const handleEditSave = async (itemId) => {
    if (!editText.trim()) return;
    try {
      await shoppingApi.update(itemId, { text: editText.trim(), quantity: editQuantity.trim() });
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, text: editText.trim(), quantity: editQuantity.trim() } : i));
      setEditingId(null);
    } catch { toast.error("Kunde inte uppdatera"); }
  };

  // Grouped projects by client
  const projectGroups = useMemo(() => {
    const groups = {};
    projects.forEach(p => {
      const client = (p.client_name || "Ovrigt").trim();
      if (!groups[client]) groups[client] = [];
      groups[client].push(p);
    });
    // Sort groups alphabetically, projects within groups alphabetically
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b, 'sv'))
      .map(([client, projs]) => ({ client, projects: projs.sort((a, b) => a.name.localeCompare(b.name, 'sv')) }));
  }, [projects]);

  const projectItems = useMemo(() =>
    selectedProject ? items.filter(i => i.project_id === selectedProject) : [],
    [items, selectedProject]);
  const undoneItems = useMemo(() =>
    projectItems.filter(i => !i.done).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [projectItems]);
  const doneItems = useMemo(() =>
    projectItems.filter(i => i.done).sort((a, b) => new Date(b.done_at || b.created_at) - new Date(a.done_at || a.created_at)),
    [projectItems]);
  const undoneByProject = useMemo(() => {
    const c = {};
    items.forEach(i => { if (!i.done) c[i.project_id] = (c[i.project_id] || 0) + 1; });
    return c;
  }, [items]);
  const currentProject = projects.find(p => p.id === selectedProject);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-5 slide-up" data-testid="shopping-list-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShoppingCart className="w-5 h-5 text-[#6dbf4a]" />
          <h1 className="text-2xl font-bold text-white tracking-tight">Inkopslista</h1>
        </div>
        <p className="text-white/25 text-sm">Material och inkop per projekt</p>
      </div>

      {/* Project selector (grouped) + Add */}
      <div className="flex gap-3 items-start flex-wrap">
        <Select value={selectedProject} onValueChange={selectProject}>
          <SelectTrigger className="w-64 bg-[#0d1218] border-white/[0.06] text-white" data-testid="project-select">
            <FolderKanban className="w-4 h-4 mr-2 text-white/40" />
            <SelectValue placeholder="Valj projekt" />
          </SelectTrigger>
          <SelectContent className="bg-[#0d1218] border-white/[0.06] max-h-72">
            {projectGroups.map(group => (
              <div key={group.client}>
                <div className="px-2 py-1.5 text-[10px] font-semibold text-white/30 uppercase tracking-wider">{group.client}</div>
                {group.projects.map(p => {
                  const count = undoneByProject[p.id] || 0;
                  return (
                    <SelectItem key={p.id} value={p.id} className="text-white pl-4" data-testid={`project-option-${p.id}`}>
                      <span className="flex items-center gap-2">
                        {p.name}
                        {count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">{count}</span>}
                      </span>
                    </SelectItem>
                  );
                })}
              </div>
            ))}
          </SelectContent>
        </Select>

        {/* Add form */}
        <form onSubmit={handleAdd} className="flex gap-2 items-center flex-1" data-testid="add-item-form">
          <Input value={newText} onChange={(e) => setNewText(e.target.value)}
            placeholder="Lagg till artikel..."
            className="bg-[#0d1218] border-white/[0.06] text-white flex-1"
            data-testid="add-item-text" />
          <Input value={newQuantity} onChange={(e) => setNewQuantity(e.target.value)}
            placeholder="Antal"
            className="bg-[#0d1218] border-white/[0.06] text-white w-20 text-center"
            data-testid="add-item-quantity" />
          <Button type="submit" disabled={!newText.trim()} className="bg-[#6dbf4a] hover:bg-[#5da83d] text-white gap-1.5"
            data-testid="add-item-btn">
            <Plus className="w-4 h-4" /> Lagg till
          </Button>
        </form>
      </div>

      {/* Project info bar */}
      {currentProject && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-white/40">
            <span className="text-white font-medium">{currentProject.name}</span>
            {currentProject.work_number && (
              <span className="text-[#6dbf4a] font-mono bg-[#6dbf4a]/10 px-1.5 py-0.5 rounded text-xs">{currentProject.work_number}</span>
            )}
            <span>{undoneItems.length} att handla</span>
          </div>
          {doneItems.length > 0 && (
            <button onClick={() => setShowDone(!showDone)}
              className={cn("flex items-center gap-1.5 text-xs transition-colors",
                showDone ? "text-[#6dbf4a]" : "text-white/25 hover:text-white/40")}
              data-testid="toggle-done-btn">
              {showDone ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showDone ? "Dolj" : "Visa"} {doneItems.length} klara
            </button>
          )}
        </div>
      )}

      {/* Items list */}
      {undoneItems.length > 0 && (
        <div className="space-y-1">
          {undoneItems.map(item => (
            <div key={item.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#0d1218] hover:bg-[#111820] transition-colors group"
              data-testid={`shopping-item-${item.id}`}>
              <button onClick={() => handleToggle(item.id)}
                className="w-5 h-5 rounded border-2 border-white/15 hover:border-[#6dbf4a] flex items-center justify-center flex-shrink-0 transition-colors"
                data-testid={`toggle-item-${item.id}`} />
              {editingId === item.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input value={editText} onChange={(e) => setEditText(e.target.value)}
                    className="bg-[#080c12] border-white/[0.06] text-white flex-1 h-8 text-sm"
                    autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(item.id); if (e.key === 'Escape') setEditingId(null); }} />
                  <Input value={editQuantity} onChange={(e) => setEditQuantity(e.target.value)}
                    className="bg-[#080c12] border-white/[0.06] text-white w-16 h-8 text-sm text-center" placeholder="Antal" />
                  <button onClick={() => handleEditSave(item.id)} className="text-[#6dbf4a] hover:text-white"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingId(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white font-medium">{item.text}</span>
                      {item.quantity && (
                        <span className="text-xs text-[#6dbf4a] bg-[#6dbf4a]/10 px-1.5 py-0.5 rounded">{item.quantity} st</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-white/25 mt-0.5">
                      <span>{item.created_by_name}</span>
                      {item.created_at && <span>· {format(new Date(item.created_at), "d MMM", { locale: sv })}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-white"
                      onClick={(e) => startEdit(item, e)} data-testid={`edit-item-${item.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-red-400"
                      onClick={() => handleDelete(item.id)} data-testid={`delete-item-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Done items */}
      {showDone && doneItems.length > 0 && (
        <div className="space-y-1">
          {doneItems.map(item => (
            <div key={item.id}
              className="flex items-center gap-3 px-3 py-2 rounded-xl opacity-40 hover:opacity-60 transition-opacity group"
              data-testid={`shopping-item-${item.id}`}>
              <button onClick={() => handleToggle(item.id)}
                className="w-5 h-5 rounded bg-[#6dbf4a] flex items-center justify-center flex-shrink-0"
                data-testid={`toggle-item-${item.id}`}>
                <Check className="w-3 h-3 text-white" />
              </button>
              <span className="flex-1 text-sm text-white line-through truncate">{item.text}</span>
              {item.quantity && <span className="text-xs text-white/40">{item.quantity} st</span>}
              <span className="text-[11px] text-white/30 hidden sm:block">{item.done_by_name}</span>
              <Button variant="ghost" size="icon"
                className="h-7 w-7 text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleDelete(item.id)} data-testid={`delete-item-${item.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* All done */}
      {undoneItems.length === 0 && doneItems.length > 0 && !showDone && (
        <div className="text-center py-12">
          <Check className="w-8 h-8 mx-auto mb-3 text-[#6dbf4a]/40" />
          <p className="text-white/40 text-sm">Allt inhandlat for {currentProject?.name}</p>
          <button onClick={() => setShowDone(true)} className="text-xs text-white/20 hover:text-[#6dbf4a] mt-2 transition-colors">
            Visa {doneItems.length} klara
          </button>
        </div>
      )}

      {/* Empty */}
      {projectItems.length === 0 && selectedProject && (
        <div className="text-center py-16">
          <ShoppingCart className="w-10 h-10 mx-auto mb-3 text-gray-700" />
          <p className="text-white/40 text-sm">Inga artiklar for {currentProject?.name}</p>
          <p className="text-white/25 text-xs mt-1">Skriv vad som behovs ovan</p>
        </div>
      )}
    </div>
  );
};

export default ShoppingList;
